import { useNavigation, useRoute } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { observer } from "mobx-react-lite";
import React from "react";
import {
  ScrollView,
  Tabs,
  useTabsContext,
  View,
  YGroup,
  YStack,
} from "tamagui";
import { Permission } from "../../../../permissions/types";
import { usePlanNavigation } from "../../../../plans/usePlanNavigation";
import { Body } from "../../../../ui/Body";
import { Box } from "../../../../ui/Box";
import { Button } from "../../../../ui/Button";
import { Label } from "../../../../ui/Label";
import { Title } from "../../../../ui/Title";
import type { BusinessPlanModel } from "../../BusinessPlanModel";
import { BusinessInfoSettingsUI } from "../../settings/BusinessInfoSettingsUI";
import { BusinessPlanSettingsBox } from "../../ui/BusinessPlanSettingsBox";
import { BusinessPlanSettingsTags } from "./BusinessPlanSettingsTags";
import { BusinessSettingsUsers } from "./BusinessSettingsUsers";

type Props = {
  businessPlan: BusinessPlanModel;
};

const Tab: React.FC<{
  label: string;
  value: string;
  businessPlan: BusinessPlanModel;
}> = (props) => {
  const tabsContext = useTabsContext();
  const TextComponent = tabsContext.value === props.value ? Label : Body;

  return (
    <Tabs.Tab
      paddingVertical={24}
      paddingHorizontal={32}
      borderRadius={0}
      focusStyle={{ backgroundColor: "$mutedBackground" }}
      value={props.value}
    >
      <TextComponent>{props.label}</TextComponent>
    </Tabs.Tab>
  );
};

export const BusinessPlanSettingsUI = observer(({ businessPlan }: Props) => {
  const navigation = useNavigation();
  const route =
    useRoute<
      NativeStackScreenProps<
        ReactNavigation.RootParamList,
        "BusinessSettings"
      >["route"]
    >();
  const { replace } = usePlanNavigation();

  if (!businessPlan.checkPermission(Permission.AccessToAdminPage)) {
    return null;
  }

  return (
    <Tabs
      defaultValue={route.params?.tab ?? "info"}
      orientation="horizontal"
      flexDirection="column"
      onValueChange={(value) => {
        replace("BusinessSettings", {
          businessUid: businessPlan.uid,
          tab: value,
        });
      }}
    >
      <YGroup gap={"$medium"}>
        <View position={"sticky" as "static"} zIndex={50} top={58}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            backgroundColor={"$background"}
          >
            <Tabs.List disablePassBorderRadius aria-label="Manage your account">
              <Tab value="info" label="Info" businessPlan={businessPlan} />
              <Tab value="tags" label="Tags" businessPlan={businessPlan} />
              <Tab
                value="members"
                label="Members"
                businessPlan={businessPlan}
              />
              <Tab value="other" label="Other" businessPlan={businessPlan} />
            </Tabs.List>
          </ScrollView>
        </View>
        <Tabs.Content value="info">
          <BusinessInfoSettingsUI businessPlan={businessPlan} />
        </Tabs.Content>
        <Tabs.Content value="tags">
          <BusinessPlanSettingsTags businessPlan={businessPlan} />
        </Tabs.Content>
        <Tabs.Content value="members">
          <BusinessSettingsUsers businessPlan={businessPlan} />
        </Tabs.Content>
        <Tabs.Content value="other">
          <Box>
            <YStack gap={"$medium"}>
              <Title>Settings</Title>

              <Button
                onPress={() => {
                  navigation.navigate("CustomerAdmin", {
                    customerUid: businessPlan.data.customerInfo.uid,
                  });
                }}
              >
                Go to Customer Admin
              </Button>
            </YStack>
          </Box>

          {!!businessPlan.data.eventInfo.settings && (
            <BusinessPlanSettingsBox
              settings={businessPlan.data.eventInfo.settings}
              onChange={(updatedSettings) => {
                businessPlan.updateEventSettings(updatedSettings);
              }}
            />
          )}
        </Tabs.Content>
      </YGroup>
    </Tabs>
  );
});
